// src/pages/sessions/SessionDetail.tsx
// Ã¢Å“â€¦ ENHANCED: Added report functionality for sessions
// Ã°Å¸â€ºÂ¡Ã¯Â¸Â PROTECTION: Block protections enforced

import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonBackButton,
  IonIcon,
  IonBadge,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonChip,
  IonSpinner,
  IonToast,
  IonAlert,
  IonActionSheet,
  IonSegment,
  IonSegmentButton,
  IonRefresher,
  IonRefresherContent,
  IonNote,
  IonModal,
  IonDatetime,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonTextarea,
} from '@ionic/react';
import {
  calendar,
  time,
  location,
  videocam,
  people,
  chatbubbles,
  settings,
  checkmarkCircle,
  closeCircle,
  helpCircle,
  timeOutline,
  share,
  trash,
  personAdd,
  pencilOutline,
  flagOutline,
  cart,
  addCircleOutline,
} from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { sessionService } from '../../services/sessionService';
import { supabase } from '../../services/supabase';
import { SessionWithDetails, RSVPStatus, SessionSupplyWithUser } from '../../types/session';
import { AvatarPreview } from '../../components/avatar/AvatarPreview';
import { updateDataWithRetry, fetchDataWithRetry } from '../../services/retryService';
import { handleError, ErrorMessages } from '../../services/errorHandler';
import { UserProfileModal } from '../../components/UserProfileModal';
import { GroupChatModal } from '../../components/GroupChatModal';
import { GroupMembersModal } from '../../components/GroupMembersModal';
import { InvitePlayersModal } from '../../components/InvitePlayersModal';
import { SuggestGameModal } from '../../components/SuggestGameModal';
import { ReportModal } from '../../components/ReportModal';
import { getGroupImageConsent } from '../../services/imageConsentService';
import './SessionDetail.css';

type TabView = 'details' | 'participants' | 'games' | 'supplies' | 'chat';

const SessionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [session, setSession] = useState<SessionWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState<TabView>('details');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRSVPSheet, setShowRSVPSheet] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [showGroupChat, setShowGroupChat] = useState(false);
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [showInvitePlayers, setShowInvitePlayers] = useState(false);
  const [showSuggestGame, setShowSuggestGame] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editLocationType, setEditLocationType] = useState<'in-person' | 'online'>('in-person');
  const [editLocationDetails, setEditLocationDetails] = useState('');
  const [showImageWarning, setShowImageWarning] = useState(false);
  const [pendingRSVPStatus, setPendingRSVPStatus] = useState<RSVPStatus | null>(null);
  const [editPlatform, setEditPlatform] = useState('');

  // Supplies state
  const [supplies, setSupplies] = useState<SessionSupplyWithUser[]>([]);
  const [newSupplyItem, setNewSupplyItem] = useState('');
  const [showAddSupply, setShowAddSupply] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
      await loadSession();
      await loadSupplies();
    };
    init();
  }, [id]);

  const loadSession = async () => {
    setLoading(true);

    try {
      const result = await fetchDataWithRetry(
        () => sessionService.getSessionById(id)
      );

      if (result.success && result.data) {
        setSession(result.data);
      } else {
        const errorMsg = result.error?.userMessage || ErrorMessages.LOAD_DATA_FAILED;
        setErrorMessage(errorMsg);
        setShowError(true);
        setSession(null);
      }
    } catch (error) {
      const appError = handleError(error, 'load session');
      setErrorMessage(appError.userMessage);
      setShowError(true);
      setSession(null);
    }

    setLoading(false);
  };

  const loadSupplies = async () => {
    try {
      const result = await sessionService.getSessionSupplies(id);
      if (result.success && result.supplies) {
        setSupplies(result.supplies);
      }
    } catch (error) {
      logger.error('Error loading supplies:', error);
    }
  };

  const handleRefresh = async (event: CustomEvent) => {
    await loadSession();
    await loadSupplies();
    event.detail.complete();
  };

  const isHost = session?.host_id === currentUserId;
  const userParticipant = session?.participants?.find(p => p.user_id === currentUserId);
  const userRSVP = userParticipant?.rsvp_status || 'pending';
  const isInvited = !!userParticipant;

  const handleRSVP = async (status: RSVPStatus) => {
    if (!isInvited) {
      setErrorMessage('You are not invited to this session');
      setShowError(true);
      return;
    }

    // If accepting, check if group has image sharing enabled and show warning
    if (status === 'accepted' && session?.group_id) {
      const imageConsent = await getGroupImageConsent(session.group_id);
      if (imageConsent.success && imageConsent.enabled) {
        // Show warning about image sharing
        setPendingRSVPStatus(status);
        setShowImageWarning(true);
        setShowRSVPSheet(false);
        return;
      }
    }

    // Proceed with RSVP
    try {
      const result = await updateDataWithRetry(
        () => sessionService.updateRSVP(id, status)
      );

      if (result.success) {
        setToastMessage(`RSVP updated to ${status}`);
        setShowToast(true);
        await loadSession();
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to update RSVP. Please try again.';
        setErrorMessage(errorMsg);
        setShowError(true);
      }
    } catch (error) {
      const appError = handleError(error, 'update RSVP');
      setErrorMessage(appError.userMessage);
      setShowError(true);
    }
    setShowRSVPSheet(false);
  };

  const confirmRSVPWithImageSharing = async () => {
    if (!pendingRSVPStatus) return;

    try {
      const result = await updateDataWithRetry(
        () => sessionService.updateRSVP(id, pendingRSVPStatus)
      );

      if (result.success) {
        setToastMessage(`RSVP updated to ${pendingRSVPStatus}`);
        setShowToast(true);
        await loadSession();
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to update RSVP. Please try again.';
        setErrorMessage(errorMsg);
        setShowError(true);
      }
    } catch (error) {
      const appError = handleError(error, 'update RSVP');
      setErrorMessage(appError.userMessage);
      setShowError(true);
    }
    setShowImageWarning(false);
    setPendingRSVPStatus(null);
  };

  const handleDeleteSession = async () => {
    try {
      const result = await updateDataWithRetry(
        () => sessionService.deleteSession(id)
      );

      if (result.success) {
        setToastMessage('Session deleted');
        setShowToast(true);
        setTimeout(() => history.replace('/tabs/sessions'), 1000);
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to delete session. Please try again.';
        setErrorMessage(errorMsg);
        setShowError(true);
      }
    } catch (error) {
      const appError = handleError(error, 'delete session');
      setErrorMessage(appError.userMessage);
      setShowError(true);
    }
  };

  const handleViewMembers = () => {
    setShowGroupChat(false);
    setShowGroupMembers(true);
  };

  const handleGroupLeft = () => {
    setShowGroupChat(false);
    history.replace('/tabs/sessions');
  };

  const handleEditSession = () => {
    if (session) {
      setEditDate(session.session_date);
      const dateStr = session.session_date;
      setEditStartTime(`${dateStr}T${session.start_time}`);
      setEditEndTime(session.end_time ? `${dateStr}T${session.end_time}` : '');
      setEditLocationType(session.location_type);
      setEditLocationDetails(session.location_details || '');
      setEditPlatform(session.platform || '');
      setShowEditModal(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!editDate || !editStartTime) {
      setErrorMessage('Date and start time are required');
      setShowError(true);
      return;
    }

    if (editLocationType === 'in-person' && !editLocationDetails.trim()) {
      setErrorMessage('Please provide location details for in-person sessions');
      setShowError(true);
      return;
    }

    if (editLocationType === 'online' && !editPlatform.trim()) {
      setErrorMessage('Please provide platform details for online sessions');
      setShowError(true);
      return;
    }

    const startTimeOnly = editStartTime.includes('T') ? editStartTime.split('T')[1] : editStartTime;
    const endTimeOnly = editEndTime && editEndTime.includes('T') ? editEndTime.split('T')[1] : editEndTime;

    try {
      const result = await updateDataWithRetry(
        () => sessionService.updateSession(id, {
          session_date: editDate,
          start_time: startTimeOnly,
          end_time: endTimeOnly || null,
          location_type: editLocationType,
          location_details: editLocationType === 'in-person' ? editLocationDetails : null,
          platform: editLocationType === 'online' ? editPlatform : null,
        })
      );

      if (result.success) {
        setToastMessage('Session updated successfully');
        setShowToast(true);
        setShowEditModal(false);
        await loadSession();
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to update session. Please try again.';
        setErrorMessage(errorMsg);
        setShowError(true);
      }
    } catch (error) {
      const appError = handleError(error, 'update session');
      setErrorMessage(appError.userMessage);
      setShowError(true);
    }
  };

  const handleReportComplete = () => {
    setShowReportModal(false);
    setToastMessage('Report submitted successfully');
    setShowToast(true);
    // Leave session after reporting
    setTimeout(() => {
      if (userRSVP !== 'declined') {
        handleRSVP('declined');
      }
      history.replace('/tabs/sessions');
    }, 1500);
  };

  // Supplies handlers
  const handleAddSupply = async () => {
    if (!newSupplyItem.trim()) {
      setErrorMessage('Please enter an item name');
      setShowError(true);
      return;
    }

    try {
      const result = await sessionService.addSupply(id, newSupplyItem);
      if (result.success) {
        setNewSupplyItem('');
        setShowAddSupply(false);
        await loadSupplies();
        setToastMessage('Supply added');
        setShowToast(true);
      } else {
        setErrorMessage(result.error || 'Failed to add supply');
        setShowError(true);
      }
    } catch (error) {
      logger.error('Error adding supply:', error);
      setErrorMessage('Failed to add supply');
      setShowError(true);
    }
  };

  const handleClaimSupply = async (supplyId: string, currentlyClaimed: boolean) => {
    try {
      const result = await sessionService.claimSupply(supplyId, !currentlyClaimed);
      if (result.success) {
        await loadSupplies();
        setToastMessage(currentlyClaimed ? 'Item unclaimed' : 'Item claimed');
        setShowToast(true);
      } else {
        setErrorMessage(result.error || 'Failed to claim item');
        setShowError(true);
      }
    } catch (error) {
      logger.error('Error claiming supply:', error);
      setErrorMessage('Failed to claim item');
      setShowError(true);
    }
  };

  const handleDeleteSupply = async (supplyId: string) => {
    try {
      const result = await sessionService.deleteSupply(supplyId);
      if (result.success) {
        await loadSupplies();
        setToastMessage('Item removed');
        setShowToast(true);
      } else {
        setErrorMessage(result.error || 'Failed to remove item');
        setShowError(true);
      }
    } catch (error) {
      logger.error('Error deleting supply:', error);
      setErrorMessage('Failed to remove item');
      setShowError(true);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getRSVPIcon = (status: RSVPStatus) => {
    switch (status) {
      case 'accepted': return checkmarkCircle;
      case 'declined': return closeCircle;
      case 'maybe': return helpCircle;
      default: return timeOutline;
    }
  };

  const getRSVPColor = (status: RSVPStatus) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'declined': return 'danger';
      case 'maybe': return 'warning';
      default: return 'medium';
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/sessions" />
            </IonButtons>
            <IonTitle>Loading...</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!session) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/sessions" />
            </IonButtons>
            <IonTitle>Session Not Found</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="empty-state">
            <h2>Session not found</h2>
            <p>This session may have been deleted or you don't have access to it.</p>
            <IonButton onClick={() => history.replace('/tabs/sessions')}>
              Back to Sessions
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const acceptedCount = session.participants?.filter(p => p.rsvp_status === 'accepted').length || 0;
  const declinedCount = session.participants?.filter(p => p.rsvp_status === 'declined').length || 0;
  const maybeCount = session.participants?.filter(p => p.rsvp_status === 'maybe').length || 0;
  const pendingCount = session.participants?.filter(p => p.rsvp_status === 'pending').length || 0;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/sessions" />
          </IonButtons>
          <IonTitle>{session.title}</IonTitle>
          <IonButtons slot="end">
            {isHost ? (
              <IonButton onClick={() => setShowActionSheet(true)}>
                <IonIcon icon={settings} />
              </IonButton>
            ) : isInvited ? (
              <IonButton onClick={() => setShowRSVPSheet(true)}>
                <IonIcon
                  icon={getRSVPIcon(userRSVP)}
                  color={getRSVPColor(userRSVP)}
                  style={userRSVP === 'accepted' ? { color: '#000' } : {}}
                />
              </IonButton>
            ) : null}
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={currentTab} onIonChange={e => setCurrentTab(e.detail.value as TabView)}>
            <IonSegmentButton value="details">
              <IonLabel>Details</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="participants">
              <IonLabel>Players ({acceptedCount})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="games">
              <IonLabel>Games</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="supplies">
              <IonLabel>Supplies</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="chat">
              <IonLabel>Chat</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {isInvited && userRSVP === 'pending' && (
          <IonCard color="primary" style={{ margin: '16px' }}>
            <IonCardContent>
              <h3 style={{ margin: '0 0 8px 0' }}>Ã°Å¸Å½â€° You're Invited!</h3>
              <p style={{ margin: '0 0 12px 0' }}>
                {session.host?.display_name || session.host?.username} invited you to this gaming session.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <IonButton size="small" color="success" onClick={() => handleRSVP('accepted')}>
                  <IonIcon icon={checkmarkCircle} slot="start" />
                  Accept
                </IonButton>
                <IonButton size="small" color="warning" onClick={() => handleRSVP('maybe')}>
                  <IonIcon icon={helpCircle} slot="start" />
                  Maybe
                </IonButton>
                <IonButton size="small" color="danger" fill="outline" onClick={() => handleRSVP('declined')}>
                  <IonIcon icon={closeCircle} slot="start" />
                  Decline
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {currentTab === 'details' && (
          <div style={{ padding: '16px' }}>
            <IonCard>
              <IonCardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <IonCardTitle>{session.title}</IonCardTitle>
                  <IonBadge color={session.status === 'confirmed' ? 'success' : 'primary'}>
                    {session.status}
                  </IonBadge>
                </div>
                {session.description && (
                  <p style={{ margin: '8px 0 0 0', color: 'var(--ion-color-medium)' }}>
                    {session.description}
                  </p>
                )}
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  <IonItem>
                    <IonIcon icon={people} slot="start" />
                    <IonLabel>
                      <h3>Host</h3>
                      <p>{session.host?.display_name || session.host?.username}</p>
                    </IonLabel>
                  </IonItem>

                  <IonItem>
                    <IonIcon icon={calendar} slot="start" />
                    <IonLabel>
                      <h3>Date</h3>
                      <p>{formatDate(session.session_date)}</p>
                    </IonLabel>
                  </IonItem>

                  <IonItem>
                    <IonIcon icon={time} slot="start" />
                    <IonLabel>
                      <h3>Time</h3>
                      <p>
                        {formatTime(session.start_time)}
                        {session.end_time && ` - ${formatTime(session.end_time)}`}
                      </p>
                    </IonLabel>
                  </IonItem>

                  <IonItem>
                    <IonIcon icon={session.location_type === 'in-person' ? location : videocam} slot="start" />
                    <IonLabel>
                      <h3>{session.location_type === 'in-person' ? 'Location' : 'Platform'}</h3>
                      <p>
                        {session.location_type === 'in-person'
                          ? session.location_details
                          : session.platform}
                      </p>
                    </IonLabel>
                  </IonItem>

                  {session.max_players && (
                    <IonItem>
                      <IonIcon icon={people} slot="start" />
                      <IonLabel>
                        <h3>Player Limit</h3>
                        <p>{acceptedCount} / {session.max_players} confirmed</p>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardHeader>
                <IonCardTitle>RSVP Summary</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <IonChip color="success">
                    <IonIcon icon={checkmarkCircle} />
                    <IonLabel>{acceptedCount} Accepted</IonLabel>
                  </IonChip>
                  <IonChip color="warning">
                    <IonIcon icon={helpCircle} />
                    <IonLabel>{maybeCount} Maybe</IonLabel>
                  </IonChip>
                  <IonChip color="medium">
                    <IonIcon icon={timeOutline} />
                    <IonLabel>{pendingCount} Pending</IonLabel>
                  </IonChip>
                  <IonChip color="danger">
                    <IonIcon icon={closeCircle} />
                    <IonLabel>{declinedCount} Declined</IonLabel>
                  </IonChip>
                </div>
              </IonCardContent>
            </IonCard>

            {isHost && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Host Actions</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonButton expand="block" onClick={() => setShowInvitePlayers(true)}>
                    <IonIcon icon={personAdd} slot="start" />
                    Invite Players
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}
          </div>
        )}

        {currentTab === 'participants' && (
          <IonList>
            {session.participants && session.participants.length > 0 ? (
              session.participants.map(participant => (
                <IonItem
                  key={participant.id}
                  button
                  onClick={() => {
                    if (participant.user_id !== currentUserId) {
                      setSelectedProfileUserId(participant.user_id);
                      setShowProfileModal(true);
                    }
                  }}
                >
                  <div slot="start">
                    <AvatarPreview
                      avatarData={participant.user?.avatar || { color: '#1E88E5' }}
                      size={40}
                      userId={participant.user_id}
                      isAdmin={participant.user?.is_admin || false}
                    />
                  </div>
                  <IonLabel>
                    <h2>{participant.user?.display_name || participant.user?.username}</h2>
                    {participant.user?.display_name && <p>@{participant.user.username}</p>}
                  </IonLabel>
                  <IonChip slot="end" color={getRSVPColor(participant.rsvp_status)}>
                    <IonIcon icon={getRSVPIcon(participant.rsvp_status)} />
                    <IonLabel>{participant.rsvp_status}</IonLabel>
                  </IonChip>
                </IonItem>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <IonNote>No participants yet</IonNote>
              </div>
            )}
          </IonList>
        )}

        {currentTab === 'games' && (
          <div style={{ padding: '16px' }}>
            <IonButton expand="block" onClick={() => setShowSuggestGame(true)}>
              Suggest a Game
            </IonButton>
            {session.games && session.games.length > 0 ? (
              <IonList style={{ marginTop: '16px' }}>
                {session.games.map(game => (
                  <IonItem key={game.id}>
                    <IonLabel>
                      <h2>{game.game?.name}</h2>
                      <p>Suggested by {game.suggested_by_user?.display_name || game.suggested_by_user?.username}</p>
                    </IonLabel>
                    <IonBadge slot="end">{game.votes} votes</IonBadge>
                  </IonItem>
                ))}
              </IonList>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <IonNote>No games suggested yet</IonNote>
              </div>
            )}
          </div>
        )}

        {currentTab === 'supplies' && (
          <div style={{ padding: '16px' }}>
            <IonButton expand="block" onClick={() => setShowAddSupply(true)}>
              <IonIcon icon={addCircleOutline} slot="start" />
              Add Supply Item
            </IonButton>
            {supplies.length > 0 ? (
              <IonList style={{ marginTop: '16px' }}>
                {supplies.map(supply => {
                  const isClaimedByMe = supply.claimed_by === currentUserId;
                  const canDelete = supply.added_by === currentUserId || isHost;

                  return (
                    <IonItem key={supply.id}>
                      <IonLabel>
                        <h2>{supply.item_name}</h2>
                        {supply.claimed_by_user ? (
                          <p style={{ color: 'var(--ion-color-success)' }}>
                            ✓ {supply.claimed_by_user.display_name || supply.claimed_by_user.username} is bringing this
                          </p>
                        ) : (
                          <p style={{ color: 'var(--ion-color-medium)' }}>
                            Not claimed yet
                          </p>
                        )}
                      </IonLabel>
                      <div slot="end" style={{ display: 'flex', gap: '8px' }}>
                        {!supply.claimed_by || isClaimedByMe ? (
                          <IonButton
                            size="small"
                            color={isClaimedByMe ? 'medium' : 'success'}
                            onClick={() => handleClaimSupply(supply.id, isClaimedByMe)}
                          >
                            {isClaimedByMe ? 'Unclaim' : 'I\'ll Bring It'}
                          </IonButton>
                        ) : null}
                        {canDelete && (
                          <IonButton
                            size="small"
                            color="danger"
                            fill="clear"
                            onClick={() => handleDeleteSupply(supply.id)}
                          >
                            <IonIcon icon={trash} slot="icon-only" />
                          </IonButton>
                        )}
                      </div>
                    </IonItem>
                  );
                })}
              </IonList>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <IonIcon icon={cart} style={{ fontSize: '64px', color: 'var(--ion-color-medium)' }} />
                <IonNote>
                  <p>No supplies added yet</p>
                  <p style={{ marginTop: '8px' }}>Add items like snacks, drinks, or game materials</p>
                </IonNote>
              </div>
            )}
          </div>
        )}

        {currentTab === 'chat' && (
          <div style={{ padding: '16px' }}>
            <IonButton expand="block" onClick={() => setShowGroupChat(true)}>
              <IonIcon icon={chatbubbles} slot="start" />
              Open Chat
            </IonButton>
          </div>
        )}

        <IonActionSheet
          isOpen={showRSVPSheet}
          onDidDismiss={() => setShowRSVPSheet(false)}
          header="Update RSVP"
          buttons={[
            {
              text: 'Accept',
              icon: checkmarkCircle,
              handler: () => handleRSVP('accepted')
            },
            {
              text: 'Maybe',
              icon: helpCircle,
              handler: () => handleRSVP('maybe')
            },
            {
              text: 'Decline',
              icon: closeCircle,
              role: 'destructive',
              handler: () => handleRSVP('declined')
            },
            {
              text: 'Report Session',
              icon: flagOutline,
              role: 'destructive',
              handler: () => setShowReportModal(true)
            },
            {
              text: 'Cancel',
              role: 'cancel'
            }
          ]}
        />

        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          header="Session Options"
          buttons={[
            {
              text: 'Edit Date, Time & Location',
              icon: pencilOutline,
              handler: () => handleEditSession()
            },
            {
              text: 'Delete Session',
              role: 'destructive',
              icon: trash,
              handler: () => setShowDeleteConfirm(true)
            },
            {
              text: 'Cancel',
              role: 'cancel'
            }
          ]}
        />

        <IonAlert
          isOpen={showDeleteConfirm}
          onDidDismiss={() => setShowDeleteConfirm(false)}
          header="Delete Session"
          message="Are you sure you want to delete this session? This action cannot be undone."
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            { text: 'Delete', role: 'destructive', handler: handleDeleteSession }
          ]}
        />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          position="bottom"
          aria-live="polite"
        />

        <IonAlert
          isOpen={showError}
          onDidDismiss={() => setShowError(false)}
          header="Error"
          message={errorMessage}
          buttons={['OK']}
        />

        <IonAlert
          isOpen={showImageWarning}
          onDidDismiss={() => {
            setShowImageWarning(false);
            setPendingRSVPStatus(null);
          }}
          header="Image Sharing Enabled"
          message="This group has image sharing enabled. By accepting this invitation, you consent to image sharing in the group chat."
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                setPendingRSVPStatus(null);
              }
            },
            {
              text: 'Accept & Continue',
              handler: confirmRSVPWithImageSharing
            }
          ]}
        />

        <IonAlert
          isOpen={showAddSupply}
          onDidDismiss={() => {
            setShowAddSupply(false);
            setNewSupplyItem('');
          }}
          header="Add Supply Item"
          message="What item do you need for this session?"
          inputs={[
            {
              name: 'item',
              type: 'text',
              placeholder: 'e.g., Snacks, Drinks, Wingspan',
              value: newSupplyItem,
              attributes: {
                maxlength: 100
              }
            }
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel'
            },
            {
              text: 'Add',
              handler: (data) => {
                setNewSupplyItem(data.item);
                handleAddSupply();
              }
            }
          ]}
        />

        <GroupChatModal
          isOpen={showGroupChat}
          onDismiss={() => setShowGroupChat(false)}
          groupId={session.group_id || ''}
          onMembersClick={handleViewMembers}
          onGroupLeft={handleGroupLeft}
        />

        <GroupMembersModal
          isOpen={showGroupMembers}
          onDismiss={() => setShowGroupMembers(false)}
          groupId={session.group_id || ''}
          groupName={session.title}
        />

        <InvitePlayersModal
          isOpen={showInvitePlayers}
          onDismiss={() => setShowInvitePlayers(false)}
          sessionId={id}
          currentParticipantIds={session.participants?.map(p => p.user_id) || []}
          onInviteComplete={() => {
            setToastMessage('Players invited!');
            setShowToast(true);
            loadSession();
          }}
        />

        <SuggestGameModal
          isOpen={showSuggestGame}
          onDismiss={() => setShowSuggestGame(false)}
          sessionId={id}
          alreadySuggestedGameIds={session?.games?.map(g => g.game_id) || []}
          onSuggestComplete={() => {
            setToastMessage('Game suggested!');
            setShowToast(true);
            loadSession();
          }}
        />

        <ReportModal
          isOpen={showReportModal}
          onDismiss={() => setShowReportModal(false)}
          reportType="session"
          reportedId={id}
          reportedName={session.title}
          onReportComplete={handleReportComplete}
        />

        <IonModal isOpen={showEditModal} onDidDismiss={() => setShowEditModal(false)} className="edit-modal">
          <IonHeader>
            <IonToolbar>
              <IonTitle>Edit Session Details</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowEditModal(false)}>Cancel</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Session Date</IonLabel>
                <IonDatetime
                  value={editDate}
                  onIonChange={e => setEditDate(e.detail.value as string)}
                  presentation="date"
                  min={new Date().toISOString()}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Start Time</IonLabel>
                <IonDatetime
                  value={editStartTime}
                  onIonChange={e => setEditStartTime(e.detail.value as string)}
                  presentation="time"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">End Time (Optional)</IonLabel>
                <IonDatetime
                  value={editEndTime}
                  onIonChange={e => setEditEndTime(e.detail.value as string)}
                  presentation="time"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Location Type</IonLabel>
                <IonSelect
                  value={editLocationType}
                  onIonChange={e => setEditLocationType(e.detail.value)}
                >
                  <IonSelectOption value="in-person">In-Person</IonSelectOption>
                  <IonSelectOption value="online">Online</IonSelectOption>
                </IonSelect>
              </IonItem>

              {editLocationType === 'in-person' ? (
                <IonItem>
                  <IonLabel position="stacked">Location Details *</IonLabel>
                  <IonTextarea
                    value={editLocationDetails}
                    onIonChange={e => setEditLocationDetails(e.detail.value || '')}
                    placeholder="Enter address or location details"
                    rows={3}
                    autocapitalize="sentences"
                  />
                </IonItem>
              ) : (
                <IonItem>
                  <IonLabel position="stacked">Platform *</IonLabel>
                  <IonInput
                    value={editPlatform}
                    onIonChange={e => setEditPlatform(e.detail.value || '')}
                    placeholder="e.g., Discord, Tabletop Simulator"
                    autocapitalize="sentences"
                  />
                </IonItem>
              )}
            </IonList>
            <div style={{ padding: '16px' }}>
              <IonButton expand="block" onClick={handleSaveEdit}>
                Save Changes
              </IonButton>
            </div>
          </IonContent>
        </IonModal>
      </IonContent>

      {/* User Profile Modal */}
      {selectedProfileUserId && (
        <UserProfileModal
          isOpen={showProfileModal}
          onDismiss={() => {
            setShowProfileModal(false);
            setSelectedProfileUserId(null);
          }}
          userId={selectedProfileUserId}
          currentUserId={currentUserId}
          showChatButton={false}
        />
      )}
    </IonPage>
  );
};

export default SessionDetail;